import { defineStore } from 'pinia'

import api from '@/api/api'
import { StorePerformance, parsePerformance } from '@/models'
import { useReservationStore } from '@/modules/reservation/stores/ReservationStore'
import { useSettingStore } from '@/stores/setting'
import { byId, currentTime, deleteById, gtmEvt, todayIsoStr, upsertById } from '@/utils/utils'

import {
  AccountingReportRead,
  PerformanceAddOnRead,
  PerformanceAddOnWrite,
  PerformanceCreate,
  PerformanceListRead,
  PerformanceQueryParams,
  PerformanceRead,
  PerformanceReportRead,
  PerformanceReservationsRead,
  PerformanceSeatingTimeWrite,
  PerformanceTableWrite,
  PerformanceWrite,
  ShowId,
} from '@generated/types'

export interface PerformanceState {
  performances: StorePerformance[]
}

export const usePerformanceStore = defineStore({
  id: 'performance',
  state: (): PerformanceState => {
    if (typeof window === 'object' && window?.__tt_preload?.performance) {
      return window.__tt_preload.performance
    } else {
      return {
        performances: [],
      }
    }
  },
  getters: {
    performanceById() {
      return (id: number): StorePerformance => {
        let performance = this.performances.find(byId(id))
        if (!performance) {
          throw new Error(`Performance ${id} is not found`)
        }
        return performance
      }
    },
    maxPerformanceDate(): string {
      return [...new Set(this.performances.map(p => p.date))].sort().pop() || '1800-01-01'
    },
  },
  actions: {
    storePerformance(rawPerformance: PerformanceRead | PerformanceListRead): StorePerformance {
      const performance = parsePerformance(rawPerformance)
      return upsertById(performance, this.performances)
    },

    async getPerformances(query: PerformanceQueryParams) {
      const response = await api.getPerformances(query || {})

      response.data.results.map(this.storePerformance)

      return {
        total: response.data.count,
        pageSize: response.data.pageSize,
      }
    },

    async getPerformanceReports(query: PerformanceQueryParams): Promise<PerformanceReportRead[]> {
      const response = await api.getPerformanceReports(query || {})

      return response.data
    },

    async getAccountingSummary(
      month: string,
      type: 'accrual' | 'cash',
    ): Promise<AccountingReportRead[]> {
      const response = await api.getAccountingSummary(month, type)
      return response.data
    },

    async getPerformance(id: number): Promise<StorePerformance> {
      const response = await api.getPerformance(id)
      return this.storePerformance(response.data)
    },

    async createPerformance(performance: PerformanceCreate) {
      gtmEvt('createPerformance')
      const response = await api.addPerformance(performance)
      return this.storePerformance(response.data)
    },

    async updatePerformance({
      id,
      date,
      time,
      show_id,
      seating_times,
      publish_date,
      publish_time,
    }: {
      id?: number
      date?: string
      time?: string
      show_id?: ShowId
      seating_times?: PerformanceSeatingTimeWrite[]
      publish_date?: string
      publish_time?: string
    }) {
      gtmEvt('updatePerformance')
      const performanceW: PerformanceWrite = {
        id,
        datetime: useSettingStore().makeDateTime({ time, date }),
        show_id,
        seating_times,
        publish_datetime: useSettingStore().makeDateTime({
          date: publish_date || todayIsoStr(),
          time: publish_time || currentTime(),
        }),
      }

      const response = await api.updatePerformance(performanceW)
      return this.storePerformance(response.data)
    },

    async deletePerformance(id: number) {
      gtmEvt('deletePerformance')
      await api.deletePerformance(id)
      deleteById(id, this.performances)
    },

    async upsertPerformanceTable({
      id,
      table_id,
      name,
      minimum_guests,
      maximum_guests,
      x,
      y,
      h,
      w,
      shape,
      price_per_person,
      type_id,
      rotation,
      held,
      standing_room,
    }: any) {
      gtmEvt('upsertPerformanceTable')
      const table: PerformanceTableWrite = {
        id: table_id,
        name,
        minimum_guests,
        maximum_guests,
        x,
        y,
        h,
        w,
        shape,
        performance: id,
        price_per_person,
        type_id,
        rotation,
        held,
        standing_room,
      }

      if (!table.type_id) delete table.type_id
      const response = !table_id
        ? await api.addPerformanceTable(table)
        : await api.updatePerformanceTable(table)
      const performance = this.performanceById(id)
      if (performance && performance.tables) {
        upsertById(response.data, performance.tables)
      }
    },

    async deletePerformanceTable({ id, table_id }: any) {
      gtmEvt('deletePerformanceTable')
      await api.deletePerformanceTable(table_id)
      const performance = this.performanceById(id)
      if (performance && performance.tables) {
        deleteById(table_id, performance.tables)
      }
    },

    async getPerformanceReservations(id: number): Promise<PerformanceReservationsRead> {
      const response = await api.getPerformanceReservations(id)
      const performance = response.data
      this.storePerformance({
        id: performance.id,
        datetime: performance.datetime,
        publish_datetime: performance.publish_datetime,
        show_id: performance.show.id,
        show: performance.show,
        tables: performance.tables,
        sold: performance.sold,
      })
      if (response.data.reservations) {
        const reservationStore = useReservationStore()
        response.data.reservations.map(reservationStore.storeReservation)
      }

      return { ...response.data }
    },

    async upsertPerformanceAddOn(
      performanceId,
      addOn: PerformanceAddOnWrite,
    ): Promise<PerformanceAddOnRead> {
      gtmEvt('updatePerformanceAddOn')
      let response
      if (addOn.id) {
        response = await api.updatePerformanceAddOn(performanceId, addOn)
      } else {
        response = await api.addPerformanceAddOn(performanceId, addOn)
      }
      try {
        const performance = this.performanceById(performanceId)
        if (performance && performance.add_ons) {
          upsertById(response.data, performance.add_ons)
        }
      } catch {
        // ignore if we weren't able to find the performance in the store
      }
      return response.data
    },

    async copyAddOnToPerformance({
      performance_id,
      add_on_id,
    }: {
      performance_id: number
      add_on_id: number
    }): Promise<PerformanceAddOnRead> {
      const response = await api.copyAddOnToPerformance({ performance_id, add_on_id })
      return response.data
    },

    async bulkPricePerformanceTables(performanceId: number, tables: number[], price: number) {
      gtmEvt('bulkPricePerformanceTables')
      const response = await api.bulkPricePerformanceTables(performanceId, { tables, price })
      return response.data
    },
  },
})
